import { Controller, useFormContext } from "react-hook-form";

import { useState, useMemo } from "react";
import {
  formatDateToMonthDayYear,
  RevButton,
  RevCard,
  RevDialog,
  RevIconButton,
  RevInput,
  RevLink,
  RevSelect,
  RevTooltip,
  RevVanillaCalendar,
} from "@revmatics/catalyst";
import {
  CampaignByIdEditBodyType,
  CampaignByIdItemDataType,
  RecommendationsItemDataType,
} from "../../../../../../../../stores/types/campaignsTypes";
import { useTrimmedRegister } from "../../../../../../../../helpers/hooks/useTrimmedRegister.ts";
import { Calendar } from "vanilla-calendar-pro";
import { campaignKPIOptions } from "../../../../../../../../pages/campaigns/data/campaignData.ts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faCircleInfo,
  faXmark,
} from "@fortawesome/pro-light-svg-icons";
import { RecommendationItem } from "../recommendations/RecommendationItem.tsx";

type Props = {
  campaignById: CampaignByIdItemDataType | null;
  setEndDate: (value: string) => void;
  setStartDate: (value: string) => void;
  startDate: string;
  endDate: string;
  recommendations: RecommendationsItemDataType[];
  setRecommendations: (value: RecommendationsItemDataType[]) => void;
};

export const CampaignFormView = ({
  setEndDate,
  setStartDate,
  startDate,
  endDate,
  recommendations,
  setRecommendations,
}: Props) => {
  const [isCustomGoalDialog, setIsCustomGoalDialog] = useState(false);
  const [isOpenCalendar, setIsOpenCalendar] = useState(false);

  const {
    register,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<CampaignByIdEditBodyType>();

  const registerTrimmed = useTrimmedRegister(register);
  const kpiResult = watch("kpi");

  // Memoize config to prevent the calendar from "re-initing" on every click
  const calendarConfig = useMemo(
    () => ({
      onClickDate(self: Calendar) {
        const dateList = self.context.selectedDates;
        if (dateList) {
          const start = dateList[0] || "";
          const end = dateList[1] || "";
          setStartDate(start);
          setEndDate(end);
          setValue("start_date", start, { shouldValidate: true });
          setValue("end_date", end, { shouldValidate: true });
        }
      },
      type: "multiple" as const,
      displayMonthsCount: 2 as const,
      selectedDates: [startDate || "", endDate || ""],
      displayDisabledDates: false,
      selectionDatesMode: "multiple-ranged" as const,
      selectedTheme: "light" as const,
    }),
    [startDate, endDate, setStartDate, setEndDate, setValue],
  );

  return (
    <RevCard title="General" paddingTitle="px-6 py-4" className="space-y-6">
      <RevInput
        name="name"
        className="w-full"
        label="Campaign Name"
        placeholder="Enter name"
        type="text"
        size="md"
        isLabelBold
        required
        register={registerTrimmed("name", {
          required: "Campaign name is required.",
        })}
        error={errors.name}
      />
      <div className="space-y-2">
        <p className="typography-body-bold-sm text-monochrome-900">
          Strategic goals and key performance indicators (KPIs)
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-4">
          <RevSelect
            name="kpi"
            label="Campaign KPI"
            options={campaignKPIOptions}
            control={control}
            error={errors.kpi}
            size="md"
            required
            placeholder="Select"
          />
          <Controller
            name="kpi_value"
            control={control}
            render={({ field }) => (
              <RevInput
                {...field}
                value={field.value || ""}
                label="Value"
                className="w-full"
                type="text"
                size="md"
                placeholder="Enter value"
                error={errors.kpi_value}
                disabled={!kpiResult}
                mask="currency"
              />
            )}
          />
        </div>
        <RevButton
          variant="tertiary"
          text="Create a Custom Goal"
          handleClick={() => setIsCustomGoalDialog(true)}
        />
      </div>
      <div className="space-y-2">
        <RevInput
          name="start_date"
          label=""
          type="hidden"
          size="md"
          register={register("start_date", { required: "Date is required." })}
          className="hidden"
        />
        <RevInput
          name="end_date"
          label=""
          type="hidden"
          size="md"
          register={register("end_date", {
            required: "Please, select end date.",
          })}
          className="hidden"
          value={endDate}
        />
        <p className="typography-body-bold-sm text-monochrome-900">Timeline</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-2 relative">
          <div className="flex flex-col gap-1">
            <label className="typography-body-xs text-monochrome-900">
              From To<span className="text-red-500 ml-0.5">*</span>
            </label>
            <button
              type="button"
              aria-label="Select Date"
              onClick={() => setIsOpenCalendar(!isOpenCalendar)}
              className={`w-full rounded border py-2.5 px-3 h-10 flex justify-between items-center gap-2 bg-white ${
                isOpenCalendar
                  ? (errors.start_date && !startDate) ||
                    (errors.end_date && !endDate)
                    ? "border-red-500"
                    : "border-primary-500"
                  : (errors.start_date && !startDate) ||
                      (errors.end_date && !endDate)
                    ? "border-red-500"
                    : "border-monochrome-200 hover:border-monochrome-800 disabled:hover:border-monochrome-200"
              }`}
            >
              <span className={`typography-body-sm text-monochrome-900`}>
                {startDate || endDate
                  ? `${startDate ? formatDateToMonthDayYear(startDate) : ""} - ${endDate ? formatDateToMonthDayYear(endDate) : ""}`
                  : "Select"}
              </span>
              <FontAwesomeIcon
                icon={faCalendar}
                className={`text-monochrome-600 text-md`}
              />
            </button>
            {((errors.start_date && !startDate) ||
              (errors.end_date && !endDate)) && (
              <p className="typography-body-xs text-red-500 text-left">
                {errors?.start_date?.message || errors?.end_date?.message}
              </p>
            )}
            {isOpenCalendar && (
              <div
                className="fixed w-screen h-screen top-0 left-0 bg-monochrome-900/20 md:bg-transparent"
                onClick={() => setIsOpenCalendar(false)}
              />
            )}
            <div
              className={
                isOpenCalendar
                  ? "fixed left-0 pb-6 px-3 md:p-0 md:absolute bottom-0 md:-translate-y-12 w-screen md:w-full md:h-fit md:min-h-[304px] shadow-elevation-1 rounded z-10 bg-white"
                  : "hidden"
              }
            >
              <div className="p-3 md:hidden flex justify-between items-center">
                <h5>Timeline</h5>
                <RevIconButton
                  icon={faXmark}
                  handleClick={() => setIsOpenCalendar(false)}
                />
              </div>
              <RevVanillaCalendar config={calendarConfig} />
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6">
        <RevInput
          name="budget"
          className="w-full"
          label="Budget"
          placeholder="Enter name"
          type="text"
          size="md"
          isLabelBold
          prependText="$"
          required
          register={registerTrimmed("budget", {
            required: "Budget is required.",
          })}
          error={errors.budget}
        />
      </div>
      {recommendations && recommendations.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <p className="typography-body-bold-sm text-monochrome-900">
              User Recommendations
            </p>
            <RevTooltip
              position="top"
              className="w-[200px] md:w-[416px]"
              content={
                <p>
                  This section should be leveraged to make user selections on
                  which platforms and spaces you would like to see activated and
                  recommended in your media plan and creator briefs.
                </p>
              }
            >
              <FontAwesomeIcon
                icon={faCircleInfo}
                className="text-monochrome-600 hover:text-primary-500"
              />
            </RevTooltip>
          </div>
          <div className="border-t border-monochrome-100">
            {recommendations.map((item) => (
              <RecommendationItem
                data={item}
                edit
                key={`recommendations-campaign-edit-${item.label}-${item.icon_class}-${item.type}`}
                handleChange={(value) => {
                  const recommendationListUpdate = recommendations.map(
                    (elem) => {
                      const newItem = {
                        ...item,
                        recommendation: value
                          ? value.toString()
                          : item.recommendation,
                      };
                      return elem.ad_platform_mapping
                        ? item.ad_platform_mapping === elem.ad_platform_mapping
                          ? newItem
                          : elem
                        : item.type === elem.type
                          ? newItem
                          : elem;
                    },
                  );
                  setRecommendations(recommendationListUpdate);
                }}
              />
            ))}
          </div>
        </div>
      )}
      <RevDialog
        isOpen={isCustomGoalDialog}
        setIsOpen={setIsCustomGoalDialog}
        title="Create a Custom Goal"
        classNameContainer="z-50"
        variant="info"
        btnList={[
          <RevButton
            key="cancel-create-custom-goal"
            text="Cancel"
            variant="secondary"
            handleClick={() => setIsCustomGoalDialog(false)}
          />,
          <RevLink
            key="contact-us-create-custom-goal"
            text="Contact Us"
            displayAs="button"
            buttonVariant="primary"
            pathname="/contact-us"
            buttonSize="medium"
          />,
        ]}
      >
        <p className="typography-body-sm text-monochrome-900 text-center">
          We want to help you customize your campaign goals. Please contact{" "}
          <a
            href="mailto:support@revmatics.ai"
            className="underline hover:no-underline"
          >
            support@revmatics.ai
          </a>{" "}
          to let one of our specialists help get you what you need.
        </p>
      </RevDialog>
    </RevCard>
  );
};
